import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/baseUi/tabs"
import { useBoxSlotsBeingViewed } from "@/modules/boxPlanning/store/useBoxPlanningStore"
import { BoxSlotFormUpdate } from "@/modules/boxSlot/components/boxSlotFormUpdate"
import { BoxSlotRegistrationInfo } from "@/modules/boxSlot/components/boxSlotRegistrationInfo"
import { BoxSlotSessionContentInfo } from "@/modules/boxSlotSession/components/boxSlotSessionContentInfo"
import { BoxStaffPermissions } from "@/modules/boxStaff/gateway/boxStaffDto"
import { useBoxStaffPermission } from "@/modules/boxStaff/hooks/useBoxStaffPermissions"
import { word } from "@/modules/core/utils/i18n"
import { cn } from "@/modules/lib/utils"
import { useSidebar } from "@/modules/sidebar/hooks/useSidebar"
import { LoadingSpinner } from "@/modules/ui/components/loading"
import { startTransition, Suspense } from "react"

type boxSlotInfoDetailsProps = {
  boxSlotId?: string
  destinationView?: "session"
  className?: string
} & React.ComponentProps<"div">

export function BoxSlotInfoDetails({ boxSlotId, destinationView, className, ...props }: boxSlotInfoDetailsProps) {
  const boxSlotBeingViewed = useBoxSlotsBeingViewed()
  const currentBoxSlot = boxSlotId ?? boxSlotBeingViewed
  const canEditSlot = useBoxStaffPermission(BoxStaffPermissions.MANAGE_SLOTS)
  const { getCurrentStackState, setCurrentStackState } = useSidebar()
  const activeIndex = getCurrentStackState("activeTabIndex") ?? (destinationView === "session" ? "1" : "0")

  function selectTab(index: string) {
    startTransition(() => {
      setCurrentStackState("activeTabIndex", index)
    })
  }

  if (!currentBoxSlot) return null

  return (
    <div className={cn("flex w-full flex-col gap-4", className)} {...props}>
      <Tabs defaultValue={activeIndex} className="flex w-full flex-col">
        <TabsList variant="default">
          <TabsTrigger variant="default" onClick={() => selectTab("0")} value="0">
            {word("box.activity.registration")}
          </TabsTrigger>
          <TabsTrigger variant="default" onClick={() => selectTab("1")} value="1">
            {word("box.activity.session")}
          </TabsTrigger>
          {canEditSlot && (
            <TabsTrigger variant="default" onClick={() => selectTab("2")} value="2">
              {word("box.activity.editSlot")}
            </TabsTrigger>
          )}
        </TabsList>
        <TabsContent value="0" className="flex w-full">
          <Suspense fallback={<LoadingSpinner />}>
            <BoxSlotRegistrationInfo boxSlotId={currentBoxSlot} />
          </Suspense>
        </TabsContent>
        <TabsContent value="1" className="flex w-full">
          <Suspense fallback={<LoadingSpinner />}>
            <BoxSlotSessionContentInfo boxSlotId={currentBoxSlot} />
          </Suspense>
        </TabsContent>
        {canEditSlot && (
          <TabsContent value="2" className="flex w-full">
            <Suspense fallback={<LoadingSpinner />}>
              <BoxSlotFormUpdate boxSlotId={currentBoxSlot} className="pt-4" />
            </Suspense>
          </TabsContent>
        )}
      </Tabs>
    </div>
  )
}
